import { useTranslation } from '@kgc/i18n';
import { Badge, Button, Input } from '@kgc/ui';
import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  Search,
  Sparkles,
  Star,
  X,
  XCircle,
} from 'lucide-react';
import type { UseReleaseFiltersReturn } from '../hooks/useReleaseFilters';

type TabId = 'changelog' | 'testing';

interface SearchFilterBarProps {
  activeTab: TabId;
  filterHook: UseReleaseFiltersReturn;
  statusCounts?: { passed: number; failed: number; not_tested: number };
}

const typeConfig = [
  {
    value: 'feature' as const,
    icon: Sparkles,
    color: 'text-blue-400 border-blue-400/50 bg-blue-400/10',
  },
  { value: 'fix' as const, icon: Bug, color: 'text-red-400 border-red-400/50 bg-red-400/10' },
  {
    value: 'improvement' as const,
    icon: Star,
    color: 'text-green-400 border-green-400/50 bg-green-400/10',
  },
  {
    value: 'breaking' as const,
    icon: AlertTriangle,
    color: 'text-orange-400 border-orange-400/50 bg-orange-400/10',
  },
] as const;

const statusConfig = [
  {
    value: 'passed' as const,
    icon: CheckCircle2,
    color: 'text-green-400 border-green-400/50 bg-green-400/10',
    label: 'releaseHub.testing.status.passed',
  },
  {
    value: 'failed' as const,
    icon: XCircle,
    color: 'text-red-400 border-red-400/50 bg-red-400/10',
    label: 'releaseHub.testing.status.failed',
  },
  {
    value: 'not_tested' as const,
    icon: HelpCircle,
    color: 'text-muted-foreground border-border/50 bg-muted/20',
    label: 'releaseHub.testing.status.not_tested',
  },
] as const;

export function SearchFilterBar({ activeTab, filterHook, statusCounts }: SearchFilterBarProps) {
  const { t } = useTranslation();
  const {
    filters,
    setQuery,
    toggleVersion,
    toggleCategory,
    toggleType,
    toggleStatus,
    setShowAllVersions,
    clearFilters,
    hasActiveFilters,
    allVersions,
    allCategories,
    changelogCount,
    testCount,
    hiddenVersionCount,
  } = filterHook;

  const count = activeTab === 'changelog' ? changelogCount : testCount;

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      {/* Row 1: Search + counter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('releaseHub.filters.search')}
            className="pl-9"
          />
          {filters.query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {t('releaseHub.filters.results', {
            filtered: count.filtered,
            total: count.total,
          })}
        </span>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-xs">
            <X className="h-3 w-3" />
            {t('releaseHub.filters.clearFilters')}
          </Button>
        )}
      </div>

      {/* Row 2: Version chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground mr-1">
          {t('releaseHub.changelog.version')}:
        </span>
        {allVersions.map(v => (
          <Badge
            key={v}
            variant="outline"
            className={`cursor-pointer text-xs transition-all ${
              filters.versions.includes(v)
                ? 'bg-primary/20 text-primary-foreground border-primary'
                : 'opacity-60 hover:opacity-100'
            }`}
            onClick={() => toggleVersion(v)}
          >
            v{v}
            {filters.versions.includes(v) && <X className="ml-1 h-3 w-3" />}
          </Badge>
        ))}
        {!filters.showAllVersions && hiddenVersionCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllVersions(true)}
            className="gap-1 text-xs text-muted-foreground"
          >
            <ChevronDown className="h-3 w-3" />
            {t('releaseHub.filters.showOlder', { count: hiddenVersionCount })}
          </Button>
        )}
      </div>

      {/* Row 3: Category chips */}
      {allCategories.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground mr-1">
            {t('releaseHub.filters.categories')}:
          </span>
          {allCategories.map(cat => (
            <Badge
              key={cat}
              variant="outline"
              className={`cursor-pointer text-xs transition-all ${
                filters.categories.includes(cat)
                  ? 'bg-accent text-accent-foreground'
                  : 'opacity-60 hover:opacity-100'
              }`}
              onClick={() => toggleCategory(cat)}
            >
              {cat}
              {filters.categories.includes(cat) && <X className="ml-1 h-3 w-3" />}
            </Badge>
          ))}
        </div>
      )}

      {/* Row 4: Tab-specific filters */}
      {activeTab === 'changelog' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground mr-1">
            {t('releaseHub.filters.types')}:
          </span>
          {typeConfig.map(({ value, icon: Icon, color }) => (
            <Badge
              key={value}
              variant="outline"
              className={`cursor-pointer gap-1 text-xs transition-all ${
                filters.types.includes(value) ? color : 'opacity-50 hover:opacity-100'
              }`}
              onClick={() => toggleType(value)}
            >
              <Icon className="h-3 w-3" />
              {t(`releaseHub.changelog.types.${value}`)}
            </Badge>
          ))}
        </div>
      )}

      {activeTab === 'testing' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground mr-1">
            {t('releaseHub.filters.statuses')}:
          </span>
          {statusConfig.map(({ value, icon: Icon, color, label }) => (
            <Badge
              key={value}
              variant="outline"
              className={`cursor-pointer gap-1 text-xs transition-all ${
                filters.statuses.includes(value) ? color : 'opacity-50 hover:opacity-100'
              }`}
              onClick={() => toggleStatus(value)}
            >
              <Icon className="h-3 w-3" />
              {t(label)}
              {statusCounts && <span className="ml-0.5 font-bold">{statusCounts[value]}</span>}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
